import * as React from 'react'
import useAppStore from '@/store/useAppStore'
import useAuthStore from '@/store/useAuthStore'
import useGetSavedViews from '@/services/api/cash-flow/useGetSavedViews'
import { resolveInitialDashboardSettings } from '@/pages/dashboard/savedViewsMath'

/**
 * Applies the dashboard's load precedence once per team, per session: the
 * user's default saved view for surface='dashboard' if one exists, otherwise
 * the team's period_type/default_horizon (returned as `meta.team_defaults`
 * alongside the view list, in the same request).
 *
 * Deliberately NOT once per mount. DashboardPage unmounts whenever you visit
 * the Table view, so a per-mount guard re-applied the defaults on every
 * return — silently discarding a horizon the user had just chosen from the
 * global bar (which is what made "switch to monthly from the table" look
 * like it did nothing at all). This is "what the dashboard loads with" for
 * the session, not something re-imposed on every navigation.
 *
 * Tracking the team it ran for means switching space still re-bootstraps,
 * since that team has its own default view and defaults.
 */
export default function useBootstrapDashboardView() {
  const { data, status } = useGetSavedViews({ surface: 'dashboard' })

  const currentTeamId = useAuthStore((s) => s.currentTeamId)
  const bootstrappedTeamId = useAppStore((s) => s.dashboardBootstrappedTeamId)
  const setBootstrappedTeamId = useAppStore((s) => s.setDashboardBootstrappedTeamId)

  const setSelectedVaultIds = useAppStore((s) => s.setDashboardSelectedVaultIds)
  const setSplitByVault = useAppStore((s) => s.setDashboardSplitByVault)
  const setNumberOfPeriods = useAppStore((s) => s.setNumberOfPeriods)
  const setPeriodType = useAppStore((s) => s.setPeriodType)

  React.useEffect(() => {
    if (status !== 'success' || bootstrappedTeamId === currentTeamId) return
    setBootstrappedTeamId(currentTeamId)

    const resolved = resolveInitialDashboardSettings(data.data, data.meta.team_defaults)
    setSelectedVaultIds(resolved.vaultIds)
    setSplitByVault(resolved.splitByVault)
    setNumberOfPeriods(resolved.horizon)
    setPeriodType(resolved.period)
  }, [
    status,
    data,
    currentTeamId,
    bootstrappedTeamId,
    setBootstrappedTeamId,
    setSelectedVaultIds,
    setSplitByVault,
    setNumberOfPeriods,
    setPeriodType,
  ])
}
